import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtDate, fmtINR, API_BASE } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, DownloadSimple, MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";

const HEALTH_COLOR = { on_track: "bg-green-100 text-green-800 border-green-300",
                       at_risk: "bg-yellow-100 text-yellow-800 border-yellow-300",
                       delayed: "bg-red-100 text-red-800 border-red-300" };
const HEALTH_DOT = { on_track: "🟢", at_risk: "🟡", delayed: "🔴" };
const STATUS_COLOR = {
  draft: "bg-slate-100 text-slate-700 border-slate-300",
  planning: "bg-blue-100 text-blue-800 border-blue-300",
  active: "bg-green-100 text-green-800 border-green-300",
  on_hold: "bg-yellow-100 text-yellow-800 border-yellow-300",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-300",
  cancelled: "bg-red-100 text-red-800 border-red-300",
};

export const HealthBadge = ({ h }) => (
  <span className={`text-xs px-2 py-0.5 rounded border ${HEALTH_COLOR[h] || ""}`}
        data-testid={`project-health-${h}`}>{HEALTH_DOT[h] || ""} {(h || "").replace(/_/g, " ")}</span>
);
export const StatusBadge = ({ s }) => (
  <span className={`text-xs px-2 py-0.5 rounded border capitalize ${STATUS_COLOR[s] || ""}`}>{(s || "").replace(/_/g, " ")}</span>
);

export default function Projects({ onlyMine = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [pms, setPms] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [filters, setFilters] = useState({ page: 1, page_size: 25 });
  const canCreate = hasRole(user, "super_admin", "hr", "finance_manager", "sales_manager", "department_manager");

  const load = () => {
    const params = { ...filters };
    if (onlyMine) params.only_mine = true;
    Object.keys(params).forEach((k) => { if (!params[k] && params[k] !== false) delete params[k]; });
    api.get("/projects", { params }).then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters, onlyMine]);
  useEffect(() => {
    api.get("/accounts").then((r) => setCustomers(r.data || [])).catch(() => {});
    api.get("/employees/lookup/all").then((r) => setPms(r.data || [])).catch(() => {});
    api.get("/projects/statuses").then((r) => { setStatuses(r.data.statuses); setPriorities(r.data.priorities); });
  }, []);

  const setF = (k, v) => setFilters({ ...filters, [k]: v === "__all__" ? "" : v, page: 1 });

  const download = async () => {
    const tok = localStorage.getItem("smipl_token");
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && k !== "page" && k !== "page_size") params.set(k, v); });
    const url = `${API_BASE}/projects/reports/export?${params}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
    const blob = await res.blob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "projects_report.xlsx"; a.click();
    URL.revokeObjectURL(a.href);
  };

  const totalPages = Math.max(1, Math.ceil(total / (filters.page_size || 25)));

  return (
    <div className="space-y-4">
      <PageHeader title={onlyMine ? "My Projects" : "All Projects"}
                  description={onlyMine ? "Projects you are managing or working on." : "All projects across the organization."}
                  action={canCreate && !onlyMine && (
                    <Button onClick={() => navigate("/projects/new")} data-testid="new-project-btn">
                      <Plus className="mr-2" /> New Project
                    </Button>
                  )} />

      <Card className="p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass className="absolute left-2 top-2.5 opacity-50" size={16} />
            <Input className="pl-8" placeholder="Search code, name, description…" value={filters.q || ""}
                   onChange={(e) => setF("q", e.target.value)} data-testid="project-search" />
          </div>
          <Select value={filters.status || "__all__"} onValueChange={(v) => setF("status", v)}>
            <SelectTrigger className="w-40" data-testid="project-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="__all__">All Statuses</SelectItem>
              {statuses.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.priority || "__all__"} onValueChange={(v) => setF("priority", v)}>
            <SelectTrigger className="w-32" data-testid="project-filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
            <SelectContent><SelectItem value="__all__">All Priorities</SelectItem>
              {priorities.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.customer_id || "__all__"} onValueChange={(v) => setF("customer_id", v)}>
            <SelectTrigger className="w-52" data-testid="project-filter-customer"><SelectValue placeholder="Customer" /></SelectTrigger>
            <SelectContent><SelectItem value="__all__">All Customers</SelectItem>
              {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.project_manager_id || "__all__"} onValueChange={(v) => setF("project_manager_id", v)}>
            <SelectTrigger className="w-52" data-testid="project-filter-pm"><SelectValue placeholder="Project Manager" /></SelectTrigger>
            <SelectContent><SelectItem value="__all__">All Managers</SelectItem>
              {pms.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.health || "__all__"} onValueChange={(v) => setF("health", v)}>
            <SelectTrigger className="w-32" data-testid="project-filter-health"><SelectValue placeholder="Health" /></SelectTrigger>
            <SelectContent><SelectItem value="__all__">All Health</SelectItem>
              <SelectItem value="on_track">🟢 On Track</SelectItem>
              <SelectItem value="at_risk">🟡 At Risk</SelectItem>
              <SelectItem value="delayed">🔴 Delayed</SelectItem>
            </SelectContent>
          </Select>
          <Input type="date" className="w-36" value={filters.start_date || ""} onChange={(e) => setF("start_date", e.target.value)} />
          <Input type="date" className="w-36" value={filters.end_date || ""} onChange={(e) => setF("end_date", e.target.value)} />
          <Button variant="outline" onClick={() => setFilters({ page: 1, page_size: filters.page_size })} data-testid="project-clear-filters">Clear</Button>
          <Button onClick={download} data-testid="project-export-btn"><DownloadSimple className="mr-2" /> Export</Button>
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/40">
            <tr>
              {["Code", "Name", "Customer", "Manager", "Start", "End", "Value", "Progress", "Health", "Status", ""]
                .map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="11" className="text-center py-10 opacity-60">No projects.</td></tr>}
            {items.map((p) => (
              <tr key={p.id} className="border-t hover:bg-slate-50 dark:hover:bg-slate-800/30 cursor-pointer"
                  onClick={() => navigate(`/projects/${p.id}`)} data-testid={`project-row-${p.code}`}>
                <td className="px-3 py-2 font-mono text-xs">{p.code}</td>
                <td className="px-3 py-2 font-medium">{p.name}</td>
                <td className="px-3 py-2 text-xs opacity-80">{p.customer_name || "—"}</td>
                <td className="px-3 py-2 text-xs opacity-80">{p.project_manager_name || "—"}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(p.start_date)}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(p.expected_end_date)}</td>
                <td className="px-3 py-2 text-xs font-mono">{fmtINR(p.project_value)}</td>
                <td className="px-3 py-2 text-xs">
                  <div className="w-24 bg-slate-200 dark:bg-slate-700 rounded h-2 overflow-hidden">
                    <div className="bg-orange-500 h-full" style={{ width: `${p.progress_pct || 0}%` }} />
                  </div>
                  <span className="text-[10px] opacity-70">{p.progress_pct || 0}%</span>
                </td>
                <td className="px-3 py-2"><HealthBadge h={p.health} /></td>
                <td className="px-3 py-2"><StatusBadge s={p.status} /></td>
                <td className="px-3 py-2"><ArrowRight size={14} className="opacity-60" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <div className="flex items-center justify-between text-sm">
        <div className="opacity-60">Showing {items.length} of {total}</div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={(filters.page || 1) <= 1}
                  onClick={() => setFilters({ ...filters, page: (filters.page || 1) - 1 })}>Prev</Button>
          <span>Page {filters.page || 1} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={(filters.page || 1) >= totalPages}
                  onClick={() => setFilters({ ...filters, page: (filters.page || 1) + 1 })}>Next</Button>
        </div>
      </div>
    </div>
  );
}
